<template>
  <div>
    <!-- Content -->
    <div class="row">
      <div class="col-xl-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">{{ $t('superAdmin.userMessages.list') }}</h5>
            <div class="d-flex gap-2">
              <button class="btn btn-outline-primary btn-sm" @click="openMaintenanceMessageWizard">
                <i class="ph-duotone ph-wrench me-2"></i>
                {{ $t('superAdmin.userMessages.createMaintenance') }}
              </button>
              <button class="btn btn-primary btn-sm" @click="openCreateModal">
                <i class="ph-duotone ph-plus me-2"></i>
                {{ $t('superAdmin.userMessages.create') }}
              </button>
            </div>
          </div>
          <div class="card-body">
            <!-- DataGrid -->
            <DataGrid
              :data="formattedMessages"
              :columns="columns"
              v-model:currentPage="currentPage"
              v-model:pageSize="pageSize"
              v-model:searchText="searchTerm"
              v-model:sortField="sortField"
              v-model:sortDirection="sortDirection"
              :loading="loading"
              :error="error"
              :empty-state-message="$t('superAdmin.userMessages.noData')"
              :search-placeholder="$t('superAdmin.userMessages.searchPlaceholder')"
              :total-items="totalItems"
              :storage-key="'userMessageManagement_columnWidths'"
              :show-refresh="true"
              @search="searchMessages"
              @pageSizeChange="handlePageSizeChange"
              @pageChange="changePage"
              @sort="handleSort"
              @refresh="loadMessages"
            >
              <!-- Filters Slot -->
              <template #filters>
                <select 
                  class="form-select form-select-sm me-2" 
                  v-model="messageTypeFilter"
                  @change="handleFilterChange"
                  style="min-width: 150px;"
                >
                  <option value="">{{ $t('superAdmin.userMessages.filter.allTypes') }}</option>
                  <option value="1">{{ $t('superAdmin.userMessages.types.oneTime') }}</option>
                  <option value="2">{{ $t('superAdmin.userMessages.types.persistent') }}</option>
                </select>
                
                <select 
                  class="form-select form-select-sm me-2" 
                  v-model="messageLevelFilter"
                  @change="handleFilterChange"
                  style="min-width: 150px;"
                >
                  <option value="">{{ $t('superAdmin.userMessages.filter.allLevels') }}</option>
                  <option value="1">{{ $t('superAdmin.userMessages.levels.info') }}</option>
                  <option value="2">{{ $t('superAdmin.userMessages.levels.warning') }}</option>
                  <option value="3">{{ $t('superAdmin.userMessages.levels.critical') }}</option>
                </select>
                
                <select 
                  class="form-select form-select-sm" 
                  v-model="activeFilter"
                  @change="handleFilterChange"
                  style="min-width: 150px;"
                >
                  <option value="">{{ $t('superAdmin.userMessages.filter.allStatus') }}</option>
                  <option value="true">{{ $t('superAdmin.userMessages.filter.active') }}</option>
                  <option value="false">{{ $t('superAdmin.userMessages.filter.inactive') }}</option>
                </select>
              </template>

              <!-- Actions Dropdown Slot -->
              <template #actions="{ item }">
                <div class="btn-group position-relative">
                  <button 
                    class="btn btn-sm btn-outline-secondary dropdown-toggle" 
                    type="button" 
                    :id="`dropdownMenuButton${item.id}`"
                    @click="toggleDropdown(item.id, $event)"
                  >
                    <i class="ph-duotone ph-dots-three-vertical"></i>
                  </button>
                  <ul 
                    class="dropdown-menu" 
                    :class="{ 'show': openDropdown === item.id }"
                    :aria-labelledby="`dropdownMenuButton${item.id}`"
                  >
                    <li>
                      <a 
                        class="dropdown-item" 
                        href="#"
                        @click.prevent="executeAction(() => editMessage(item._original))"
                      >
                        <i class="ph-duotone ph-pencil me-2"></i>
                        {{ $t('superAdmin.userMessages.actions.edit') }}
                      </a>
                    </li>
                    <li>
                      <a 
                        class="dropdown-item" 
                        href="#"
                        @click.prevent="executeAction(() => toggleActive(item._original))"
                      >
                        <i class="ph-duotone ph-power me-2"></i>
                        {{ item._original.isActive ? $t('superAdmin.userMessages.actions.deactivate') : $t('superAdmin.userMessages.actions.activate') }}
                      </a>
                    </li>
                    <li><hr class="dropdown-divider"></li>
                    <li>
                      <a 
                        class="dropdown-item text-danger" 
                        href="#"
                        @click.prevent="executeAction(() => deleteMessage(item._original))"
                      >
                        <i class="ph-duotone ph-trash me-2"></i>
                        {{ $t('superAdmin.userMessages.actions.delete') }}
                      </a>
                    </li>
                  </ul>
                </div>
              </template>

              <!-- Custom column templates -->
              <template #cell-content="{ value }">
                <span :title="value">{{ truncateContent(value, 100) }}</span>
              </template>
            </DataGrid>
            
            <!-- Color Legend -->
            <div class="mt-3">
              <small class="text-muted fw-semibold d-block mb-2">{{ $t('common.legend') }}:</small>
              <div class="d-flex flex-column gap-2">
                <div class="d-flex align-items-center gap-2">
                  <span class="badge bg-danger">{{ $t('common.expired') }}</span>
                  <small class="text-muted">{{ $t('superAdmin.userMessages.legend.expired') }}</small>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <span class="badge bg-secondary">{{ $t('common.inactive') }}</span>
                  <small class="text-muted">{{ $t('superAdmin.userMessages.legend.inactive') }}</small>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <span class="badge bg-success">{{ $t('common.active') }}</span>
                  <small class="text-muted">{{ $t('superAdmin.userMessages.legend.active') }}</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Message Form Modal -->
    <div 
      class="modal fade" 
      id="messageModal" 
      tabindex="-1" 
      ref="messageModal"
      data-bs-backdrop="static"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ editingMessage ? $t('superAdmin.userMessages.edit') : $t('superAdmin.userMessages.create') }}
            </h5>
            <button type="button" class="btn-close" @click="closeModal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveMessage">
              <div class="row">
                <div class="col-12 mb-3">
                  <label class="form-label">
                    {{ $t('superAdmin.userMessages.form.title') }}
                    <span class="text-danger">*</span>
                  </label>
                  <input 
                    type="text" 
                    class="form-control" 
                    v-model="formData.title"
                    :placeholder="$t('superAdmin.userMessages.form.titlePlaceholder')"
                    maxlength="200"
                    required
                  >
                </div>

                <div class="col-12 mb-3">
                  <label class="form-label">
                    {{ $t('superAdmin.userMessages.form.content') }}
                    <span class="text-danger">*</span>
                  </label>
                  <textarea 
                    class="form-control" 
                    v-model="formData.content"
                    :placeholder="$t('superAdmin.userMessages.form.contentPlaceholder')"
                    rows="4"
                    maxlength="1000"
                    required
                  ></textarea>
                  <small class="text-muted">{{ formData.content?.length || 0 }}/1000</small>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="form-label">
                    {{ $t('superAdmin.userMessages.form.messageType') }}
                    <span class="text-danger">*</span>
                  </label>
                  <select class="form-select" v-model.number="formData.messageType" required>
                    <option value="">{{ $t('common.select') }}</option>
                    <option :value="1">{{ $t('superAdmin.userMessages.types.oneTime') }}</option>
                    <option :value="2">{{ $t('superAdmin.userMessages.types.persistent') }}</option>
                  </select>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="form-label">
                    {{ $t('superAdmin.userMessages.form.messageLevel') }}
                    <span class="text-danger">*</span>
                  </label>
                  <select class="form-select" v-model.number="formData.messageLevel" required>
                    <option value="">{{ $t('common.select') }}</option>
                    <option :value="1">{{ $t('superAdmin.userMessages.levels.info') }}</option>
                    <option :value="2">{{ $t('superAdmin.userMessages.levels.warning') }}</option>
                    <option :value="3">{{ $t('superAdmin.userMessages.levels.critical') }}</option>
                  </select>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="form-label">
                    {{ $t('superAdmin.userMessages.form.startDate') }}
                    <span class="text-danger">*</span>
                  </label>
                  <input 
                    type="datetime-local" 
                    class="form-control" 
                    v-model="formData.startDate"
                    required
                  >
                </div>

                <div class="col-md-6 mb-3">
                  <label class="form-label">
                    {{ $t('superAdmin.userMessages.form.endDate') }}
                    <span class="text-danger">*</span>
                  </label>
                  <input 
                    type="datetime-local" 
                    class="form-control" 
                    v-model="formData.endDate"
                    required
                  >
                </div>

                <div class="col-md-6 mb-3">
                  <label class="form-label">
                    {{ $t('superAdmin.userMessages.form.priority') }}
                    <span class="text-danger">*</span>
                  </label>
                  <input 
                    type="number" 
                    class="form-control" 
                    v-model.number="formData.priority"
                    :placeholder="$t('superAdmin.userMessages.form.priorityPlaceholder')"
                    min="0"
                    required
                  >
                </div>

                <div class="col-md-6 mb-3 d-flex align-items-end">
                  <div class="form-check">
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      v-model="formData.isActive"
                      id="isActiveCheck"
                    >
                    <label class="form-check-label" for="isActiveCheck">
                      {{ $t('superAdmin.userMessages.form.isActive') }}
                    </label>
                  </div>
                </div>
              </div>

              <div v-if="formError" class="alert alert-danger">
                {{ formError }}
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              {{ $t('superAdmin.userMessages.actions.cancel') }}
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="saveMessage"
              :disabled="saving"
            >
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              {{ $t('superAdmin.userMessages.actions.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { Modal } from 'bootstrap';
import Swal from 'sweetalert2';
import superAdminService from '@/services/superAdminService';
import DataGrid from '@/components/DataGrid.vue';
import { useDateFormatter } from '@/composables/useDateFormatter';

const store = useStore();
const { t } = useI18n();
const { formatDateTime, formatLongDate, formatTimeWithLocale } = useDateFormatter();

// State
const messages = ref([]);
const loading = ref(false);
const error = ref(null);
const saving = ref(false);
const formError = ref(null);

// Pagination
const currentPage = ref(1);
const pageSize = ref(10);
const totalItems = ref(0);

// Sorting
const sortField = ref('creationTime');
const sortDirection = ref('desc');

// Filters
const searchTerm = ref('');
const messageTypeFilter = ref('');
const messageLevelFilter = ref('');
const activeFilter = ref('');

// Dropdown
const openDropdown = ref(null);

// Modal
const messageModal = ref(null);
let modalInstance = null;
const editingMessage = ref(null);

// Form data
const formData = ref({
  title: '',
  content: '',
  messageType: '',
  messageLevel: '',
  priority: 0,
  startDate: '',
  endDate: '',
  isActive: true
});

// Helpers
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Columns
const columns = ref([
  { key: 'title', label: t('superAdmin.userMessages.columns.title'), sortable: true, width: '180px' },
  { key: 'content', label: t('superAdmin.userMessages.columns.content'), sortable: false, width: '280px' },
  { 
    key: 'messageType', 
    label: t('superAdmin.userMessages.columns.messageType'), 
    sortable: true, 
    width: '120px',
    type: 'badge',
    badgeMap: {
      [t('superAdmin.userMessages.types.oneTime')]: 'badge bg-primary',
      [t('superAdmin.userMessages.types.persistent')]: 'badge bg-secondary'
    }
  },
  { 
    key: 'messageLevel', 
    label: t('superAdmin.userMessages.columns.messageLevel'), 
    sortable: true, 
    width: '120px',
    type: 'badge',
    badgeMap: {
      [t('superAdmin.userMessages.levels.info')]: 'badge bg-info',
      [t('superAdmin.userMessages.levels.warning')]: 'badge bg-warning',
      [t('superAdmin.userMessages.levels.critical')]: 'badge bg-danger'
    }
  },
  { key: 'startDate', label: t('superAdmin.userMessages.columns.startDate'), sortable: true, width: '160px' },
  { key: 'endDate', label: t('superAdmin.userMessages.columns.endDate'), sortable: true, width: '160px' },
  { key: 'priority', label: t('superAdmin.userMessages.columns.priority'), sortable: true, width: '100px' },
  { 
    key: 'isActive', 
    label: t('superAdmin.userMessages.columns.isActive'), 
    sortable: true, 
    width: '120px',
    type: 'badge',
    badgeMap: {
      [t('common.active')]: 'badge bg-success',
      [t('common.inactive')]: 'badge bg-secondary',
      [t('common.expired')]: 'badge bg-danger'
    }
  },
  { key: 'creationTime', label: t('superAdmin.userMessages.columns.creationTime'), sortable: true, width: '160px' }
]);

// Check if message is expired
const isMessageExpired = (message) => {
  if (!message.endDate) return false;
  return new Date(message.endDate) < new Date();
};

// Get row class based on message status
const getRowClass = (message) => {
  if (isMessageExpired(message)) {
    return 'table-danger'; // Expired - light red
  }
  if (!message.isActive) {
    return 'table-secondary'; // Inactive - gray
  }
  return ''; // Active - default
};

// Formatted messages for DataGrid
const formattedMessages = computed(() => {
  return messages.value.map(message => {
    const expired = isMessageExpired(message);
    let statusLabel;
    if (expired) {
      statusLabel = t('common.expired');
    } else if (message.isActive) {
      statusLabel = t('common.active');
    } else {
      statusLabel = t('common.inactive');
    }
    
    return {
      ...message,
      messageType: getMessageTypeLabel(message.messageType),
      messageLevel: getMessageLevelLabel(message.messageLevel),
      isActive: statusLabel,
      startDate: formatDateTime(message.startDate),
      endDate: formatDateTime(message.endDate),
      creationTime: formatDateTime(message.creationTime),
      _rowClass: getRowClass(message),
      _original: message
    };
  });
});

// Helper functions
const getMessageTypeLabel = (type) => {
  switch(type) {
    case 1: return t('superAdmin.userMessages.types.oneTime');
    case 2: return t('superAdmin.userMessages.types.persistent');
    default: return 'Unknown';
  }
};

const getMessageLevelLabel = (level) => {
  switch(level) {
    case 1: return t('superAdmin.userMessages.levels.info');
    case 2: return t('superAdmin.userMessages.levels.warning');
    case 3: return t('superAdmin.userMessages.levels.critical');
    default: return 'Unknown';
  }
};

const truncateContent = (text, maxLength = 100) => {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

// Load messages
const loadMessages = async () => {
  loading.value = true;
  error.value = null;
  
  try {
    const sorting = `${sortField.value} ${sortDirection.value}`;
    const response = await superAdminService.searchUserMessages({
      page: currentPage.value - 1,
      pageSize: pageSize.value,
      searchText: searchTerm.value,
      sorting: sorting,
      messageType: messageTypeFilter.value ? parseInt(messageTypeFilter.value) : null,
      messageLevel: messageLevelFilter.value ? parseInt(messageLevelFilter.value) : null,
      isActive: activeFilter.value === '' ? null : activeFilter.value === 'true'
    });
    
    messages.value = response.items || [];
    totalItems.value = response.totalCount || 0;
  } catch (err) {
    console.error('Error loading messages:', err);
    error.value = t('common.errors.generic');
  } finally {
    loading.value = false;
  }
};

// Search
const searchMessages = (term) => {
  searchTerm.value = term;
  currentPage.value = 1;
  loadMessages();
};

// Pagination
const changePage = (page) => {
  currentPage.value = page;
  loadMessages();
};

const handlePageSizeChange = (newSize) => {
  pageSize.value = newSize;
  currentPage.value = 1;
  loadMessages();
};

// Sorting
const handleSort = ({ field, direction }) => {
  sortField.value = field;
  sortDirection.value = direction;
  loadMessages();
};

// Filters
const handleFilterChange = () => {
  currentPage.value = 1;
  loadMessages();
};

// Dropdown
const toggleDropdown = (id, event) => {
  event.stopPropagation();
  openDropdown.value = openDropdown.value === id ? null : id;
};

const executeAction = (action) => {
  openDropdown.value = null;
  action();
};

// Modal functions
const openCreateModal = () => {
  editingMessage.value = null;
  formData.value = {
    title: '',
    content: '',
    messageType: '',
    messageLevel: '',
    priority: 0,
    startDate: '',
    endDate: '',
    isActive: true
  };
  formError.value = null;
  
  if (!modalInstance) {
    modalInstance = new Modal(messageModal.value);
  }
  modalInstance.show();
};

// Quick wizard to create a standard system maintenance message
const openMaintenanceMessageWizard = async () => {
  const now = new Date();
  const defaultStart = formatDateForInput(now.toISOString());
  const defaultEnd = formatDateForInput(new Date(now.getTime() + 10 * 60 * 1000).toISOString());

  const { value: result } = await Swal.fire({
    title: t('superAdmin.userMessages.maintenance.title'),
    html: `
      <div class="text-start">
        <label class="form-label fw-semibold">${t('superAdmin.userMessages.maintenance.startLabel')}</label>
        <input id="maintenance-start" type="datetime-local" class="swal2-input" value="${defaultStart}" />
        <label class="form-label fw-semibold mt-2">${t('superAdmin.userMessages.maintenance.endLabel')}</label>
        <input id="maintenance-end" type="datetime-local" class="swal2-input" value="${defaultEnd}" />
        <small class="text-muted d-block mt-2">${t('superAdmin.userMessages.maintenance.dateHint')}</small>
      </div>
    `,
    focusConfirm: false,
    showCancelButton: true,
    confirmButtonText: t('superAdmin.userMessages.maintenance.generateButton'),
    cancelButtonText: t('superAdmin.userMessages.actions.cancel'),
    preConfirm: () => {
      const start = document.getElementById('maintenance-start')?.value;
      const end = document.getElementById('maintenance-end')?.value;

      if (!start) {
        Swal.showValidationMessage(t('superAdmin.userMessages.maintenance.validation.startRequired'));
        return null;
      }
      if (!end) {
        Swal.showValidationMessage(t('superAdmin.userMessages.maintenance.validation.endRequired'));
        return null;
      }

      const startDate = new Date(start);
      const endDate = new Date(end);
      if (endDate <= startDate) {
        Swal.showValidationMessage(t('superAdmin.userMessages.maintenance.validation.endAfterStart'));
        return null;
      }

      return { start, end };
    }
  });

  if (!result) {
    return;
  }

  const startDateIso = new Date(result.start).toISOString();
  const endDateIso = new Date(result.end).toISOString();

  const dateLabel = formatLongDate(startDateIso);
  const startTimeLabel = formatTimeWithLocale(startDateIso, { includeSeconds: false });
  const endTimeLabel = formatTimeWithLocale(endDateIso, { includeSeconds: false });

  const generatedTitle = t('superAdmin.userMessages.maintenance.generatedTitle', {
    date: dateLabel,
    startTime: startTimeLabel,
    endTime: endTimeLabel
  });

  const generatedContent = t('superAdmin.userMessages.maintenance.generatedContent', {
    date: dateLabel,
    startTime: startTimeLabel,
    endTime: endTimeLabel
  });

  // Message Start Date = now (so users see the message immediately)
  // Message End Date = maintenance end (message disappears after maintenance)
  const nowForInput = formatDateForInput(new Date().toISOString());

  editingMessage.value = null;
  formError.value = null;
  formData.value = {
    title: generatedTitle,
    content: generatedContent,
    messageType: 2,      // Persistent
    messageLevel: 2,     // Warning
    priority: 100,
    startDate: nowForInput,
    endDate: result.end,
    isActive: true
  };

  if (!modalInstance) {
    modalInstance = new Modal(messageModal.value);
  }
  modalInstance.show();
};

const editMessage = (message) => {
  editingMessage.value = message;
  
  formData.value = {
    title: message.title,
    content: message.content,
    messageType: message.messageType,
    messageLevel: message.messageLevel,
    priority: message.priority,
    startDate: formatDateForInput(message.startDate),
    endDate: formatDateForInput(message.endDate),
    isActive: message.isActive
  };
  formError.value = null;
  
  if (!modalInstance) {
    modalInstance = new Modal(messageModal.value);
  }
  modalInstance.show();
};

const closeModal = () => {
  if (modalInstance) {
    modalInstance.hide();
  }
  editingMessage.value = null;
  formError.value = null;
};

const validateForm = () => {
  if (!formData.value.title) {
    return t('superAdmin.userMessages.form.validation.titleRequired');
  }
  if (!formData.value.content) {
    return t('superAdmin.userMessages.form.validation.contentRequired');
  }
  if (!formData.value.messageType) {
    return t('superAdmin.userMessages.form.validation.messageTypeRequired');
  }
  if (!formData.value.messageLevel) {
    return t('superAdmin.userMessages.form.validation.messageLevelRequired');
  }
  if (formData.value.priority === '' || formData.value.priority === null) {
    return t('superAdmin.userMessages.form.validation.priorityRequired');
  }
  if (!formData.value.startDate) {
    return t('superAdmin.userMessages.form.validation.startDateRequired');
  }
  if (!formData.value.endDate) {
    return t('superAdmin.userMessages.form.validation.endDateRequired');
  }
  
  const startDate = new Date(formData.value.startDate);
  const endDate = new Date(formData.value.endDate);
  if (endDate <= startDate) {
    return t('superAdmin.userMessages.form.validation.endDateAfterStart');
  }
  
  return null;
};

const saveMessage = async () => {
  formError.value = null;
  
  const validationError = validateForm();
  if (validationError) {
    formError.value = validationError;
    return;
  }
  
  saving.value = true;
  
  try {
    const payload = {
      title: formData.value.title,
      content: formData.value.content,
      messageType: formData.value.messageType,
      messageLevel: formData.value.messageLevel,
      priority: formData.value.priority,
      startDate: new Date(formData.value.startDate).toISOString(),
      endDate: new Date(formData.value.endDate).toISOString(),
      isActive: formData.value.isActive
    };
    
    if (editingMessage.value) {
      await superAdminService.updateUserMessage(editingMessage.value.id, payload);
      store.dispatch('notify', {
        message: t('superAdmin.userMessages.messages.updateSuccess'),
        type: 'success'
      });
    } else {
      await superAdminService.createUserMessage(payload);
      store.dispatch('notify', {
        message: t('superAdmin.userMessages.messages.createSuccess'),
        type: 'success'
      });
    }
    
    closeModal();
    loadMessages();
  } catch (err) {
    console.error('Error saving message:', err);
    formError.value = err.response?.data?.message || t('common.errors.generic');
  } finally {
    saving.value = false;
  }
};

const toggleActive = async (message) => {
  try {
    const payload = {
      ...message,
      isActive: !message.isActive
    };
    
    await superAdminService.updateUserMessage(message.id, payload);
    
    store.dispatch('notify', {
      message: message.isActive 
        ? t('superAdmin.userMessages.messages.deactivateSuccess')
        : t('superAdmin.userMessages.messages.activateSuccess'),
      type: 'success'
    });
    
    loadMessages();
  } catch (err) {
    console.error('Error toggling active status:', err);
    store.dispatch('notify', {
      message: t('common.errors.generic'),
      type: 'error'
    });
  }
};

const deleteMessage = async (message) => {
  const result = await Swal.fire({
    title: t('superAdmin.userMessages.messages.deleteConfirm'),
    text: message.title,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#dc3545',
    cancelButtonColor: '#6c757d',
    confirmButtonText: t('superAdmin.userMessages.actions.delete'),
    cancelButtonText: t('superAdmin.userMessages.actions.cancel')
  });
  
  if (result.isConfirmed) {
    try {
      await superAdminService.deleteUserMessage(message.id);
      store.dispatch('notify', {
        message: t('superAdmin.userMessages.messages.deleteSuccess'),
        type: 'success'
      });
      loadMessages();
    } catch (err) {
      console.error('Error deleting message:', err);
      store.dispatch('notify', {
        message: t('common.errors.generic'),
        type: 'error'
      });
    }
  }
};

// Close dropdown when clicking outside
const handleClickOutside = (event) => {
  if (!event.target.closest('.dropdown')) {
    openDropdown.value = null;
  }
};

// Lifecycle
onMounted(() => {
  loadMessages();
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  if (modalInstance) {
    modalInstance.dispose();
  }
});
</script>

<style>
.dropdown-menu {
  min-width: 200px;
  max-width: 280px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid #e9ecef;
  background-color: white;
  position: fixed !important;
  z-index: 9999 !important;
  margin-top: 4px;
}
</style>

<style scoped>
.form-check-input:checked {
  background-color: var(--bs-primary);
  border-color: var(--bs-primary);
}

/* Row color coding for message status */
:deep(.table-danger) {
  background-color: #f8d7da !important;
}

:deep(.table-secondary) {
  background-color: #e2e3e5 !important;
}

:deep(.table-danger td),
:deep(.table-secondary td) {
  border-color: rgba(0, 0, 0, 0.1);
}
</style>
